<template>
    <div class="my-4">
        <div class="flex items-center justify-between">
            <label
                v-if="label"
                class="mb-1 block text-sm font-medium"
                :for="name"
                >{{ $t(label) }}
                <span
                    v-if="required === true"
                    class="text-danger-500"
                    >*</span
                ></label
            >
            <Tooltip
                v-if="help"
                class="ml-2"
                bg="dark"
                size="md"
                position="left"
            >
                <div class="text-sm text-gray-200">{{ $t(help) }}</div>
            </Tooltip>
        </div>
        <div class="flex">
            <input
                :id="name"
                v-model="displayValue"
                :name="name"
                class="form-input w-full"
                type="text"
                :disabled="disabled"
                :readonly="readonly"
                @keydown="onKeyDown"
                @blur="onBlur"
            />
            <span
                v-if="unit"
                class="ml-2 pt-2"
                >{{ unit }}</span
            >
        </div>
        <div
            v-if="bottomHelpText"
            class="mt-2 text-xs"
        >
            {{ $t(bottomHelpText) }}
        </div>
    </div>
</template>
<script setup lang="ts">
import { Tooltip } from '@src/components'
import { numberFiledValueParsing } from '@src/const'
import { i18n } from '@src/utils/lang'
import { useVModels } from '@vueuse/core'
import { ref, watch } from 'vue'

const { locale } = i18n.global

const props = defineProps<{
    name: string
    label?: string
    help?: string
    bottomHelpText?: string | null
    required?: boolean
    modelValue: number | null
    unit?: string
    min?: number
    max?: number
    disabled?: boolean
    readonly?: boolean
    maximumFractionDigits?: number
    allowNull?: boolean
}>()

const emit = defineEmits(['update:modelValue'])

const { modelValue, min, max } = useVModels(props, emit)

function format(value: number | null) {
    if (value == null || value === ('' as unknown)) return ''
    // modelValue can arrive as a decimal string from the API (e.g. "9.00000").
    // String.prototype.toLocaleString ignores locale/options and returns the
    // string as-is, which then gets misparsed by the active locale (e.g. the
    // "." is read as a thousands separator under "de", turning 9 into 900000).
    const numericValue = typeof value === 'number' ? value : Number(value)
    if (Number.isNaN(numericValue)) return ''
    return numericValue.toLocaleString(locale.value, {
        maximumFractionDigits: props.maximumFractionDigits ?? 4,
    })
}

const displayValue = ref(format(modelValue.value))

function onKeyDown(event: KeyboardEvent) {
    if (
        !(
            /[0-9,.]$/.test(event.key) ||
            event.key === 'Backspace' ||
            event.key === 'Enter' ||
            (event.ctrlKey && event.key === 'a')
        )
    ) {
        event.preventDefault()
    }
}

function onBlur() {
    displayValue.value = format(modelValue.value)
}

// Only the raw text typed by the user matters here; reformatting on
// every keystroke (e.g. via a computed) strips the decimal separator
// as soon as it's typed, since parseFloat('1.') === 1.
watch(displayValue, (value) => {
    if (!value) {
        modelValue.value = props.allowNull ? null : 0
        return
    }
    if (!/[0-9,.]$/.test(value)) return

    let numValue = numberFiledValueParsing[locale.value](value)
    if (Number.isNaN(numValue)) return

    if (max?.value != null && numValue > max.value) {
        numValue = max.value
        displayValue.value = numValue.toString()
        return
    }
    if (min?.value != null && numValue < min.value) {
        numValue = min.value
        displayValue.value = numValue.toString()
        return
    }

    modelValue.value = numValue
})

// Keep the displayed text in sync when modelValue changes from outside
// (initial load, parent reset) rather than from the user's own typing.
watch(modelValue, (value) => {
    const currentValue = displayValue.value
        ? numberFiledValueParsing[locale.value](displayValue.value)
        : props.allowNull
        ? null
        : 0

    if (currentValue !== value) {
        displayValue.value = format(value)
    }
})

watch(locale, () => {
    displayValue.value = format(modelValue.value)
})
</script>
